import type { Commands } from "@/api/commands";
import type { StrategyInfoResponse } from "@/lib/strategy-info";

/**
 * When a backtest runs its strategy on a bar besides the bar's close:
 * TradingView's "Recalculate" properties, as the server's flags. 0 is on bar
 * close. The server never reads them from the script: the client resolves the
 * script's declaration and sends them with every create, update and preview.
 */
export const RECALC_ON_ORDER_FILLS = 1;
export const RECALC_ON_REALTIME_BARS = 2;


type Request = Pick<Commands, "getStrategyInfo">;

/** The recalculation a strategy declares (calc_on_order_fills, calc_on_every_tick). */
export function declaredRecalculate(resp: StrategyInfoResponse | null | undefined): number {
  const bt = resp?.info?.backtest;
  if (!bt) return 0;
  return (bt.calc_on_order_fills ? RECALC_ON_ORDER_FILLS : 0) | (bt.calc_on_every_tick ? RECALC_ON_REALTIME_BARS : 0);
}

/** TradingView's names for the flags set, or "On bar close". */
export function recalculateLabel(r: number | null | undefined): string {
  const names: string[] = [];
  if ((r ?? 0) & RECALC_ON_ORDER_FILLS) names.push("After order is filled");
  if ((r ?? 0) & RECALC_ON_REALTIME_BARS) names.push("On every tick");
  return names.length ? names.join(", ") : "On bar close";
}

/**
 * The recalculation the script behind a strategy config declares. `source` is
 * the script text or its pinned script:// reference. When the server can't
 * say (no source, a declaration it rejects, a failed request) the fallback is
 * kept, so a run never silently changes what a backtest already stores.
 */
export async function resolveRecalculate(
  request: Request,
  strategyProvider: string | undefined,
  strategyConfig: Record<string, unknown> | null | undefined,
  fallback: number | null | undefined = 0
): Promise<number> {
  const kept = fallback ?? 0;
  const provider = strategyProvider || "pinescript";
  if (provider === "pinescript" && !String(strategyConfig?.source || "").trim()) return kept;
  try {
    const resp = (await request.getStrategyInfo({
      strategy_provider: provider,
      strategy_config: strategyConfig || {},
    })) as StrategyInfoResponse | null;
    return resp?.status === "ok" ? declaredRecalculate(resp) : kept;
  } catch {
    return kept;
  }
}
